import { Component, forwardRef, Output, EventEmitter, input, effect, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { CommonModule } from '@angular/common';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { NgZone } from '@angular/core'; // Import NgZone
import { HttpClient } from '@angular/common/http';

declare const google: any; // Declare google to avoid TypeScript errors

@Component({
  selector: 'app-location-search',
  templateUrl: './location-search.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LocationSearchComponent),
      multi: true
    }
  ],
  imports: [
    CommonModule,
    FormsModule,
    AutoCompleteModule,
  ]
})
export class LocationSearchComponent implements ControlValueAccessor, OnInit {

  searchPlaceResults: any[] = [];
  selectedValue: any;
  public value = input<any>();

  @Output() placeSelected = new EventEmitter<google.maps.places.Place>();

  private onChange: (value: any) => void = () => { };
  onTouched: () => void = () => { };

  private geocoderService: google.maps.Geocoder | undefined;
  private autocompleteService: google.maps.places.AutocompleteService | undefined;
  private mapsInitializationPromise: Promise<boolean> | null = null;

  constructor(private http: HttpClient, private ngZone: NgZone) {
    effect(() => {
      const val = this.value();
      if (val !== undefined) {
        this.selectedValue = val;
      }
    });
  }

  ngOnInit(): void {
    void this.ensureMapsServicesReady();
  }

  writeValue(value: any): void {
    this.selectedValue = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  async searchPlaces(event: { query: string; }) {
    const query = event.query.trim();
    const coordRegex = /^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/;
    const isCoordinates = coordRegex.exec(query);

    if (!(await this.ensureMapsServicesReady()) || !this.geocoderService) {
      console.warn('Google Maps services not initialized yet.');
      this.searchPlaceResults = [];
      return;
    }

    if (query.startsWith('https://maps.app.goo.gl/')) {
      const extractorApiUrl = `https://google-maps-url-extractor-113166165184.europe-west1.run.app?shortUrl=${encodeURIComponent(query)}`;
      this.http.get<{ lat: string, lng: string }>(extractorApiUrl).subscribe({
        next: (response) => {
          if (response.lat && response.lng) {
            const latLng = new google.maps.LatLng(parseFloat(response.lat), parseFloat(response.lng));
            this.geocoderService?.geocode({ location: latLng }, (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
              this.ngZone.run(() => { // Ensure Angular change detection runs
                if (status === google.maps.GeocoderStatus.OK && results) {
                  this.searchPlaceResults = results.map(result => ({
                    name: result.formatted_address,
                    place: result
                  }));
                } else {
                  this.searchPlaceResults = [];
                }
              });
            });
          }
        },
        error: () => this.performAutocompleteSearch(query)
      });
    } else if (isCoordinates) {
      const lat = parseFloat(isCoordinates[1]);
      const lng = parseFloat(isCoordinates[2]);
      const latLng = new google.maps.LatLng(lat, lng);
      this.geocoderService?.geocode({ location: latLng }, (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
        this.ngZone.run(() => {
          if (status === google.maps.GeocoderStatus.OK && results) {
            this.searchPlaceResults = results.map(result => ({
              name: result.formatted_address,
              place: result
            }));
          } else {
            this.searchPlaceResults = [];
          }
        });
      });
    } else if (query.includes('+')) {
      this.geocoderService?.geocode({ address: query }, (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
        this.ngZone.run(() => {
          if (status === google.maps.GeocoderStatus.OK && results) {
            this.searchPlaceResults = results.map(result => ({
              name: result.formatted_address,
              place: result
            }));
          } else {
            this.performAutocompleteSearch(query);
          }
        });
      });
    } else {
      this.performAutocompleteSearch(query);
    }
  }

  async performAutocompleteSearch(query: string) {
    if (!query || !(await this.ensureMapsServicesReady()) || !this.autocompleteService) {
      this.searchPlaceResults = [];
      return;
    }

    this.autocompleteService.getPlacePredictions(
      {
        input: query,
        componentRestrictions: { country: 'sa' }
      },
      (predictions: google.maps.places.AutocompletePrediction[] | null, status: google.maps.places.PlacesServiceStatus) => {
        this.ngZone.run(() => {
          if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions) {
            this.searchPlaceResults = [];
            return;
          }

          this.searchPlaceResults = predictions.map((prediction) => ({
            name: prediction.description,
            prediction
          }));
        });
      }
    );
  }

  async onSelect(event: { originalEvent: Event, value: any }) {
    const selectedValue = event.value;

    if (selectedValue && selectedValue.place) { // From Geocoder result
      this.handlePlaceSelection(selectedValue.place);
    } else if (selectedValue && selectedValue.prediction) { // From Autocomplete prediction
      if (!(await this.ensureMapsServicesReady())) {
        return;
      }

      this.geocodePredictionSelection(selectedValue.prediction);
    }
  }

  private geocodePredictionSelection(prediction: google.maps.places.AutocompletePrediction): void {
    if (!this.geocoderService) {
      this.handlePlaceSelection({
        formattedAddress: prediction.description,
        displayName: prediction.structured_formatting?.main_text,
        id: prediction.place_id
      });
      return;
    }

    this.geocoderService.geocode({ placeId: prediction.place_id }, (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
      this.ngZone.run(() => {
        const result = status === google.maps.GeocoderStatus.OK ? results?.[0] : null;
        if (result) {
          const selectedPlace = {
            ...result,
            displayName: prediction.structured_formatting?.main_text || prediction.description,
            id: prediction.place_id
          };
          this.handlePlaceSelection(selectedPlace);
          return;
        }

        this.handlePlaceSelection({
          formattedAddress: prediction.description,
          displayName: prediction.structured_formatting?.main_text,
          id: prediction.place_id
        });
      });
    });
  }

  private handlePlaceSelection(place: any) {
    this.selectedValue = place.formattedAddress ?? place.formatted_address ?? '';
    this.onChange(this.selectedValue);
    this.onTouched();
    this.placeSelected.emit(place);
  }

  private async ensureMapsServicesReady(): Promise<boolean> {
    if (this.geocoderService && this.autocompleteService) {
      return true;
    }

    if (!this.mapsInitializationPromise) {
      this.mapsInitializationPromise = this.initializeMapsServices();
    }

    return this.mapsInitializationPromise;
  }

  private async initializeMapsServices(): Promise<boolean> {
    try {
      if (typeof google === 'undefined' || !google.maps) {
        await this.waitForGoogleMapsScript();
      }

      if (typeof google === 'undefined' || !google.maps) {
        return false;
      }

      if (typeof google.maps.importLibrary === 'function') {
        await google.maps.importLibrary('geocoding');
        await google.maps.importLibrary('places');
      }

      if (typeof google.maps.Geocoder !== 'function' || typeof google.maps.places?.AutocompleteService !== 'function') {
        return false;
      }

      this.geocoderService = new google.maps.Geocoder();
      this.autocompleteService = new google.maps.places.AutocompleteService();
      return true;
    } catch (error) {
      console.error('Failed to initialize Google Maps services.', error);
      return false;
    }
  }

  private waitForGoogleMapsScript(timeoutMs = 10000): Promise<void> {
    return new Promise((resolve, reject) => {
      const start = Date.now();

      const check = () => {
        if (typeof google !== 'undefined' && google.maps) {
          resolve();
          return;
        }

        if (Date.now() - start >= timeoutMs) {
          reject(new Error('Google Maps script did not load in time.'));
          return;
        }

        window.setTimeout(check, 100);
      };

      check();
    });
  }
}
